import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./cart.css";
import toast from "react-hot-toast";
import { clearCartItems, getCartItems } from "../../util/Cart";
import {
  getProductById,
  getWallet,
  placeOrder,
  verifyAndUpdatePaymentStatus,
} from "../../services/auth/User";
import Loader2 from "../../components/Loader2";
import logo from "../../assets/Images/logo.png";
import { FaTruck } from "react-icons/fa";

function Checkout() {
  const { mode } = useParams();
  const [isPending, setIsPending] = useState(true);
  const navigate = useNavigate(); // For redirecting if user is not logged in
  const location = useLocation();
  const [userData, setUserData] = useState(null);
  const [step, setStep] = useState(1); // Track current step
  const [orderItems, setOrderItems] = useState([]); // Stores selected products
  const [walletBalance, setWalletBalance] = useState(); // Store wallet balance
  const [totalPayment, setTotalPayment] = useState(0);

  // Address State (Prefilled from localStorage if available)
  const [address, setAddress] = useState({
    name: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });

  const [paymentMethod, setPaymentMethod] = useState("");

  useEffect(() => {
    // Retrieve user data from localStorage
    const storedUser = localStorage.getItem("userData");
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUserData(parsedUser);

      // Pre-fill address if available
      if (parsedUser.user) {
        setAddress({
          name: parsedUser.user.name || "",
          email: parsedUser.user.email || "",
          phone: parsedUser.user.phone || "",
          street: parsedUser.user.address || "",
          city: "",
          state: "",
          zip: "",
        });

        fetchWalletBalance();
      }
    } else {
      toast.error("Please log in to continue with checkout!");
      navigate("/login");
    }
  }, [navigate]);

  const fetchWalletBalance = async () => {
    try {
      const wallet = await getWallet();
      //   console.log(wallet)
      setWalletBalance(wallet?.wallet?.balance || 0);
    } catch (error) {
      toast.error("Failed to fetch wallet balance.");
    } finally {
      setIsPending(false);
    }
  };

  // Load cart items or fetch product details based on mode
  useEffect(() => {
    const state = location.state;
    if (mode !== "cart" && state && state.productDetails) {
      // Setup the order item with the provided state
      const productDetails = state.productDetails;
      const options = state.options;
      setOrderItems([{ product: productDetails, quantity: 1, options: options }]);
    } else {
      // Load all cart items if mode is "cart"
      const cartItems = getCartItems();
      setOrderItems(cartItems);
    }
  }, [mode, location.state]);


  // Validate all fields before moving to the next step
  const validateAddress = () => {
    // Check for missing fields
    if (
      !address.name ||
      !address.phone ||
      !address.street ||
      !address.city ||
      !address.state ||
      !address.zip
    ) {
      toast.error("Please fill in all address fields before continuing!");
      return false;
    }

    // Check if the pincode is a number
    if (isNaN(Number(address.zip))) {
      toast.error("Please enter a valid pincode!");
      return false;
    }

    // Additional validation can be added here if necessary
    return true;
  };

  // Move to next step after validation
  const handleNext = () => {
    if (step === 1) {
      if (!validateAddress()) return; // Stop if validation fails
    }
    setStep(step + 1);
  };

  // Move to previous step
  const handleBack = () => setStep(step - 1);

  const handlePayment = async () => {
    setIsPending(true);
    try {
      if (orderItems.length === 0) {
        toast.error("No items in the order!");
        return;
      }
  
      const totalPayment = orderItems.reduce(
        (total, item) => total + item.quantity * item.product.variantPrice,
        0
      );
  
      if (!userData || !userData.user) {
        toast.error("User data not found! Please log in.");
        navigate("/login");
        return;
      }
  
      if (!address.street || !address.city || !address.state || !address.zip || !address.phone) {
        toast.error("Please fill in all required address fields!");
        return;
      }
  
      if (paymentMethod === "") {
        toast.error("Please choose a payment method before proceeding.");
        return;
      }
  
      if (paymentMethod === "CGTCoins" && totalPayment > 1500) {
        toast.error("CGT Bonus cannot be used for orders exceeding ₹1500 per day.");
        return;
      }
      
      const orderData = {
        userId: userData.user._id,
        shippingInfo: {
          address: address.street,
          city: address.city,
          state: address.state,
          country: address.country || "India",
          pincode: address.zip,
          phoneNo: address.phone,
        },
        orderItems: orderItems.map(item => ({
          productId: item.product._id,
          quantity: item.quantity,
          sellingPrice: item.product.variantPrice,
          options: item.options
        })),
        paymentInfo: {
          mode: paymentMethod,
          status: "Pending",
        },
        totalAmount: totalPayment
      };
  
      if (paymentMethod === "CGTCoins") {
        if (walletBalance < totalPayment) {
          toast.error("Insufficient Balance");
          return;
        }
        const response = await placeOrder(orderData);
        console.log("Order Response:", response);
        if ( response && response.success ) {
          toast.success("Order placed successfully!");
          if (mode == "cart") {
            clearCartItems();
          }
          navigate("/orders");
        } else {
          toast.error(response.message || "Failed to place order.");
        }
      } else if (paymentMethod === "Razor") {
        const response = await placeOrder(orderData);
        // console.log(response);
        verifyRazor(response.order, response.razorpayOrder);
      } else if (paymentMethod === "COD") {
        const response = await placeOrder(orderData);
        // console.log(response);
        toast.success("Order placed successfully!");
        if (mode == "cart") {
          clearCartItems();
        }
        navigate("/orders");
      } else {
        toast.error("Other payment methods coming soon!");
      }

    } catch (error) {
      console.error("Error placing order:", error);
      toast.error("Something went wrong! Please try again.");
    } finally {
      setIsPending(false);
    }
  };
  


  const verifyRazor = async (order, razorpayOrder) => {
    const options = {
      key: "rzp_live_OnDKn72OS8Eqm1",
      amount: order.totalAmount * 100, // Amount should be in the smallest currency unit (paise)
      currency: "INR",
      name: "CGT Homes",
      description: "Delivering Happiness to your Homes",
      image: "https://example.com/logo.png", // Replace with your logo URL
      order_id: razorpayOrder.id,
      prefill: {
        name: order.customerName, // Ensure this data is available in 'order'
        email: order.customerEmail, // Ensure this data is available in 'order'
        contact: order.customerPhone, // Ensure this data is available in 'order'
      },
      notes: {
        address: "Razorpay Corporate Office",
      },
      theme: {
        color: "#1d3c53",
      },
      handler: async function (response) {
        try {
          // console.log(order._id, response.razorpay_order_id, response.razorpay_payment_id, response.razorpay_signature);
          await verifyAndUpdatePaymentStatus(
            order._id,
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature
          );
          toast.success("Payment successful.");
          if (mode == "cart") {
            clearCartItems();
          }
          navigate("/orders");
        } catch (error) {
          toast.error("Failed to update payment status.");
        }
      },
      modal: {
        ondismiss: function () {
          toast.error("Payment not completed.");
        },
      },
    };

    const razor = new window.Razorpay(options);
    razor.on("payment.failed", async function (response) {
      try {
        // console.log(order._id, response.razorpay_order_id, response.razorpay_payment_id);
        await verifyAndUpdatePaymentStatus(
          order._id,
          response.razorpay_order_id,
          response.razorpay_payment_id
        );
        toast.error(`Payment Failed: ${response.error.description}`);
        navigate("/orders");
      } catch (error) {
        toast.error("Failed to update payment status after failure.");
      }
    });
    razor.open();
  };

  useEffect(() => {
    const total = orderItems.reduce(
      (acc, item) => acc + item.quantity * item.product.variantPrice,
      0
    );
    const updatedTotal = total < 300 ? total + 49 : total;
    setTotalPayment(updatedTotal);
  }, [orderItems]);

  return (
    <>
      {isPending && <Loader2 />}

      <div className="checkout">
        <div className="checkout-container">
          <h1>Place Order - {mode}</h1>

          {/* Step Indicator */}
          <div className="checkout-steps">
            <div className={`step ${step === 1 ? "active" : ""}`}>Address</div>
            <div className={`step ${step === 2 ? "active" : ""}`}>
              Order Summary
            </div>
            <div className={`step ${step === 3 ? "active" : ""}`}>Payment</div>
          </div>

          <div className="checkout-cards">
            {step === 1 && (
              <div className="checkout-card">
                <h2>📍 User and Address</h2>
                <form className="form-1">
                  <div className="form-line">
                    <div className="form-input input-f">
                      <label>Full Name</label>
                      <input
                        type="text"
                        placeholder="Enter your name"
                        disabled
                        value={address.name}
                        required
                      />
                    </div>
                  </div>
                  <div className="form-line">
                    <div className="form-input input-f">
                      <label>Mobile Number</label>
                      <input
                        type="text"
                        placeholder="Enter your Mobile"
                        value={address.phone}
                        onChange={(e) =>
                          setAddress({ ...address, phone: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="form-line">
                    <div className="form-input input-f">
                      <label>Street Address</label>
                      <textarea
                        type="text"
                        placeholder="Enter your Street Address"
                        value={address.street}
                        onChange={(e) =>
                          setAddress({ ...address, street: e.target.value })
                        }
                        required
                      ></textarea>
                    </div>
                  </div>
                  <div className="form-line">
                    <div className="form-input input-h">
                      <label>City</label>
                      <input
                        type="text"
                        placeholder="Enter your City"
                        value={address.city}
                        onChange={(e) =>
                          setAddress({ ...address, city: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="form-input input-h">
                      <label>State</label>
                      <input
                        type="text"
                        placeholder="Enter your State"
                        value={address.state}
                        onChange={(e) =>
                          setAddress({ ...address, state: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="form-line">
                    <div className="form-input input-f">
                      <label>Zip Code</label>
                      <input
                        type="text"
                        placeholder="Enter your Zip Code"
                        value={address.zip}
                        onChange={(e) =>
                          setAddress({ ...address, zip: e.target.value })
                        }
                        required
                      />
                    </div>
                  </div>
                </form>
                <div className="form-1 btn-group">
                  <button className="form-btn-1 btn-f" onClick={handleNext}>
                    Continue to Order Summary
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="checkout-card">
                <h2>🛒 Order Summary</h2>
                {orderItems.length > 0 ? (
                  <>
                    {orderItems.map((item) => (
                      <div key={item.product._id} className="order-summary-item">
                      <img
                        className="order-summary-image"
                        src={item.product.imageSrc[0] || "https://www.vallabhcomponent.com/assets/images/default.jpg"}
                        alt={item.product.imageAltText?.[0] || "Product"}
                      />
                      <div className="order-summary-details">
                        <p>{item.product.title}</p>
                        <ul>
                          {item.options && item.options.map((option, index) => (
                            <li key={index}>{option}</li>  // Display each selected option
                          ))}
                        </ul>
                        <p className="quantity-price">{item.quantity} x Rs {item.product.variantPrice}</p>
                       
                      </div>
                    </div>
                    
                    ))}
                    <div className="checkout-2">
                      <p className={totalPayment >= 300 ? "free-shipping" : ""}>
                        {totalPayment < 300 ? (
                          "Delivery Charges: Rs 49"
                        ) : (
                          <>
                            <FaTruck /> Free shipping from CGTHomes
                          </>
                        )}
                      </p>

                      <h3 className="total-h3">
                        Total Amount to Pay: Rs {totalPayment}
                      </h3>
                    </div>
                  </>
                ) : (
                  <p>No items in the order.</p>
                )}
                <div className="form-1 btn-group">
                  <button className="form-btn-1 btn-h" onClick={handleBack}>
                    Go Back
                  </button>
                  <button className="form-btn-1 btn-f" onClick={handleNext}>
                    Continue to Payment
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="checkout-card">
                <h2>💳 Payment</h2>
                <h3 className="total-amount">
                  Total Amount - Rs {totalPayment}
                </h3>
                <form className="form-1">
                  <div className="form-line">
                    <div className="form-input input-f select-input">
                      <label>Payment Method</label>
                      <select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="form-select"
                      >
                        <option value="">Choose a Payment Method</option>
                        <option value="CGTCoins">CGT Bonus</option>
                        <option value="Razor">UPI / Cards</option>
                        <option value="COD">Cash On Delivery</option>
                      </select>
                    </div>
                  </div>
                </form>

                {/* Sub-cards for selected payment method */}
                {paymentMethod === "CGTCoins" &&
                  userData &&
                  userData.wallet && (
                    <div className="payment-sub-card">
                      <p>Use your CGT Bonus for this purchase.</p>
                      <p>
                        <strong>Available Balance:</strong> {walletBalance} CGT
                        Bonus
                      </p>
                    </div>
                  )}

                {paymentMethod === "upi-cards" && (
                  <div className="payment-sub-card">
                    <h3>💳 Payment Method: UPI / Cards</h3>
                    <p>Coming Soon</p>
                  </div>
                )}

                <div className="form-1 btn-group">
                  <button className="form-btn-1 btn-h" onClick={handleBack}>
                    Go Back
                  </button>
                  <button className="form-btn-1 btn-f" onClick={handlePayment}>
                    {paymentMethod === "CGTCoins"
                      ? "Pay with CGT Bonus"
                      : paymentMethod === "Razor"
                      ? "Pay with Razorpay"
                      : "Place Order"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Checkout;
